import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { ShieldCheck, Plus, FileCheck, Award, Send, CalendarClock, Pill, PawPrint, Check, Receipt } from 'lucide-react';
import bookingsApi, { type Booking, type BookingStatus, type StatusHistoryEntry } from '@/api/bookings';
import { usePets } from '@/pet/src/context/PetsContext';
import { toast } from 'sonner';
import { BookingFlow } from '@/pet/src/components/appointments/BookingFlow';
import { RescheduleAppointmentModal } from '@/pet/src/components/appointments/RescheduleAppointmentModal';
import { apiClient } from '@/api/axios';
import { QRCodeCanvas } from '@/pet/src/components/shared/QRCodeCanvas';
import { listMyPrescriptions, type Prescription } from '@/api/prescriptions';
import { PrescriptionDetailView } from '@/pet/src/components/prescriptions/PrescriptionDetailView';
import { listMyInvoices, type Invoice } from '@/api/invoices';
import { InvoiceDetailView } from '@/pet/src/components/invoices/InvoiceDetailView';

/** Convert "HH:MM AM/PM" (or already-24h "HH:MM") to 24h "HH:MM" for the backend. */
function to24h(time: string): string {
  const t = (time || '').trim();
  if (!/\s(AM|PM)$/i.test(t)) return t; // already 24h
  const [hm, period] = t.split(/\s+/);
  const [hStr, mStr] = hm.split(':');
  let h = parseInt(hStr, 10) || 0;
  if (period.toUpperCase() === 'PM' && h < 12) h += 12;
  if (period.toUpperCase() === 'AM' && h === 12) h = 0;
  return `${h.toString().padStart(2, '0')}:${(mStr || '00').padStart(2, '0')}`;
}

export const Route = createFileRoute('/dashboard/appointments')({
  component: PetsoAppointments,
});

// ─── Status config ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<BookingStatus, { label: string; color: string; bgColor: string; icon: string }> = {
  new: { label: 'New', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-900/20', icon: '📋' },
  pending_owner_confirmation: { label: 'Awaiting your confirmation', color: 'text-yellow-700 dark:text-yellow-300', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', icon: '🕒' },
  pending_assignment: { label: 'Pending Assignment', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', icon: '⏳' },
  assigned: { label: 'Assigned', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20', icon: '👤' },
  confirmed: { label: 'Confirmed', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', icon: '✅' },
  in_progress: { label: 'In Progress', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20', icon: '🔄' },
  completed: { label: 'Completed', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20', icon: '🎉' },
  cancelled: { label: 'Cancelled', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20', icon: '❌' },
  no_show: { label: 'No Show', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20', icon: '⚠️' },
  rescheduled: { label: 'Rescheduled', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', icon: '🔄' },
};

// ─── Status timeline steps ──────────────────────────────────────────────────

const ACTIVE_FLOW_STEPS: BookingStatus[] = ['new', 'assigned', 'confirmed', 'in_progress', 'completed'];

function getStepIndex(status: BookingStatus): number {
  const idx = ACTIVE_FLOW_STEPS.indexOf(status);
  return idx >= 0 ? idx : -1;
}

// ─── Filter Tabs ────────────────────────────────────────────────────────────

type FilterTab = 'today' | 'upcoming' | 'active' | 'past' | 'all';

// ─── Component ──────────────────────────────────────────────────────────────

function PetsoAppointments() {
  const { t } = useTranslation();
  const [bookingsList, setBookingsList] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusHistory, setStatusHistory] = useState<Record<string, StatusHistoryEntry[]>>({});
  const [activeFilter, setActiveFilter] = useState<FilterTab>('today');
  const [selectedPetFilter, setSelectedPetFilter] = useState<string | null>(null);
  const [petMenuOpen, setPetMenuOpen] = useState(false);
  const { pets } = usePets();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [showBooking, setShowBooking] = useState(false);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [viewingCert, setViewingCert] = useState<any>(null);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [viewingPrescription, setViewingPrescription] = useState<Prescription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [requestingAction, setRequestingAction] = useState<string | null>(null);
  const [reschedulingBooking, setReschedulingBooking] = useState<Booking | null>(null);

  const fetchBookings = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await bookingsApi.list({ page, limit: 20 });
      setBookingsList(res.bookings);
      setTotalPages(res.pagination.totalPages);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      toast.error(t('appointments_fetch_error', 'Failed to load appointments'));
    } finally {
      setIsLoading(false);
    }
  }, [page, activeFilter, t]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Auto-refresh every 30 seconds for live tracking
  useEffect(() => {
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  // Fetch certificates for completed bookings
  useEffect(() => {
    apiClient.get('/certificates').then(({ data }) => {
      setCertificates(data.certificates || []);
    }).catch(() => {});
  }, [bookingsList]);

  // Fetch prescriptions issued to the user's pets
  useEffect(() => {
    listMyPrescriptions()
      .then((rows) => setPrescriptions(rows))
      .catch(() => {});
  }, [bookingsList]);

  // Fetch invoices billed to the pet owner
  useEffect(() => {
    listMyInvoices()
      .then((rows) => setInvoices(rows))
      .catch(() => {});
  }, [bookingsList]);

  const handleRequestAction = async (bookingId: string, action: 'request-completion' | 'request-certificate') => {
    setRequestingAction(bookingId);
    try {
      await apiClient.post(`/bookings/${bookingId}/${action}`);
      toast.success(action === 'request-completion' ? 'Completion requested' : 'Certificate requested');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Request failed');
    } finally {
      setRequestingAction(null);
    }
  };

  const loadHistory = async (bookingId: string) => {
    if (statusHistory[bookingId]) return;
    try {
      const res = await bookingsApi.getById(bookingId);
      setStatusHistory((prev) => ({ ...prev, [bookingId]: res.statusHistory }));
    } catch (err) {
      console.error('Failed to load booking detail:', err);
    }
  };

  const toggleExpand = (bookingId: string) => {
    if (expandedId === bookingId) {
      setExpandedId(null);
    } else {
      setExpandedId(bookingId);
      loadHistory(bookingId);
    }
  };

  const handleCancel = async (bookingId: string) => {
    setCancellingId(bookingId);
    try {
      await bookingsApi.cancel(bookingId, 'Cancelled by pet owner');
      toast.success(t('appointments_cancelled', 'Appointment cancelled'));
      fetchBookings();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('appointments_cancel_error', 'Failed to cancel'));
    } finally {
      setCancellingId(null);
    }
  };

  // Filter bookings
  const now = new Date();
  const selectedPet = selectedPetFilter
    ? pets.find((p) => p.id === selectedPetFilter) ?? null
    : null;
  const filteredBookings = bookingsList.filter((b) => {
    // Pet filter (applies on top of status filter).
    // Match by petId when available, otherwise fall back to petName so that
    // legacy bookings (created without a linked pet row) still filter.
    if (selectedPetFilter) {
      const idMatches = b.petId && b.petId === selectedPetFilter;
      const nameMatches =
        selectedPet &&
        (b.petName ?? '').trim().toLowerCase() ===
          selectedPet.name.trim().toLowerCase();
      if (!idMatches && !nameMatches) return false;
    }
    const bookingDate = new Date(`${b.scheduledDate}T${b.scheduledTime}`);
    switch (activeFilter) {
      case 'today': {
        const todayStr = new Date().toISOString().split('T')[0];
        return b.scheduledDate === todayStr;
      }
      case 'upcoming':
        return !['completed', 'cancelled', 'no_show'].includes(b.status) && bookingDate >= now;
      case 'active':
        return ['assigned', 'confirmed', 'in_progress'].includes(b.status);
      case 'past':
        return ['completed', 'cancelled', 'no_show'].includes(b.status) || bookingDate < now;
      default:
        return true;
    }
  });

  const selectedPetObj = selectedPet;

  // Apply the pet filter to the base list used for tab counts so Today /
  // Upcoming / ... numbers reflect the selected pet.
  const petScopedList = bookingsList.filter((b) => {
    if (!selectedPetFilter) return true;
    const idMatches = b.petId && b.petId === selectedPetFilter;
    const nameMatches =
      selectedPet &&
      (b.petName ?? '').trim().toLowerCase() ===
        selectedPet.name.trim().toLowerCase();
    return Boolean(idMatches || nameMatches);
  });

  const filterTabs: { key: FilterTab; label: string; count: number }[] = [
    {
      key: 'today',
      label: t('appointments_today', 'Today'),
      count: petScopedList.filter((b) => b.scheduledDate === new Date().toISOString().split('T')[0]).length,
    },
    {
      key: 'upcoming',
      label: t('appointments_upcoming', 'Upcoming'),
      count: petScopedList.filter((b) => !['completed', 'cancelled', 'no_show'].includes(b.status) && new Date(`${b.scheduledDate}T${b.scheduledTime}`) >= now).length,
    },
    {
      key: 'active',
      label: t('appointments_active', 'Active'),
      count: petScopedList.filter((b) => ['assigned', 'confirmed', 'in_progress'].includes(b.status)).length,
    },
    {
      key: 'past',
      label: t('appointments_past', 'Past'),
      count: petScopedList.filter((b) => ['completed', 'cancelled', 'no_show'].includes(b.status)).length,
    },
    {
      key: 'all',
      label: t('appointments_all', 'All'),
      count: petScopedList.length,
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('appointments_title', 'My Appointments')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('appointments_subtitle', 'Track your pet care bookings and their status')}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => fetchBookings()}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            title={t('appointments_refresh', 'Refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowBooking(true)}
            style={{ backgroundColor: '#594BFF', color: '#fff', padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus style={{ width: '18px', height: '18px' }} />
            Book Appointment
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 items-center">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveFilter(tab.key); setPage(1); }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
            style={activeFilter === tab.key
              ? { backgroundColor: '#594BFF', color: '#ffffff', border: '2px solid #594BFF' }
              : { backgroundColor: '#ffffff', color: '#4b5563', border: '2px solid #e5e7eb' }
            }
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full"
                style={activeFilter === tab.key
                  ? { backgroundColor: 'rgba(255,255,255,0.25)', color: '#ffffff' }
                  : { backgroundColor: '#e5e7eb', color: '#4b5563' }
                }>
                {tab.count}
              </span>
            )}
          </button>
        ))}

        {/* Pets filter dropdown — sits after the All tab */}
        {pets.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setPetMenuOpen((o) => !o)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
              style={selectedPetFilter
                ? { backgroundColor: '#594BFF', color: '#ffffff', border: '2px solid #594BFF' }
                : { backgroundColor: '#ffffff', color: '#4b5563', border: '2px solid #e5e7eb' }
              }
            >
              <PawPrint className="w-4 h-4" />
              {selectedPetObj
                ? selectedPetObj.name
                : t('appointments_filter_pets', 'Pets')}
              <ChevronDown className="w-4 h-4" />
            </button>
            {petMenuOpen && (
              <>
                {/* Click-away overlay */}
                <div
                  onClick={() => setPetMenuOpen(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                />
                <div
                  className="bg-white dark:bg-[#1A1929] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg"
                  style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: '220px', zIndex: 31, padding: '6px', maxHeight: '320px', overflowY: 'auto' }}
                >
                  <button
                    type="button"
                    onClick={() => { setSelectedPetFilter(null); setPetMenuOpen(false); setPage(1); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-gray-50 dark:hover:bg-white/5"
                    style={{ color: selectedPetFilter === null ? '#594BFF' : '#4b5563', fontWeight: selectedPetFilter === null ? 600 : 500 }}
                  >
                    <PawPrint className="w-4 h-4" />
                    <span className="flex-1">{t('appointments_filter_all_pets', 'All pets')}</span>
                    {selectedPetFilter === null && <Check className="w-4 h-4" />}
                  </button>
                  {pets.map((pet) => (
                    <button
                      key={pet.id}
                      type="button"
                      onClick={() => { setSelectedPetFilter(pet.id); setPetMenuOpen(false); setPage(1); }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-gray-50 dark:hover:bg-white/5"
                      style={{ color: selectedPetFilter === pet.id ? '#594BFF' : '#4b5563', fontWeight: selectedPetFilter === pet.id ? 600 : 500 }}
                    >
                      <span className="text-base" aria-hidden>
                        {pet.species === 'cat' ? '🐈' : pet.species === 'dog' ? '🐕' : '🐾'}
                      </span>
                      <span className="flex-1 truncate">{pet.name}</span>
                      {selectedPetFilter === pet.id && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bookings List */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#2D1F7B]" />
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            {t('appointments_empty', 'No appointments found')}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            {t('appointments_empty_hint', 'Book an appointment with a pet care provider to get started')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => {
            const config = STATUS_CONFIG[booking.status];
            const isExpanded = expandedId === booking.id;
            const canCancel = !['in_progress', 'completed', 'cancelled', 'no_show'].includes(booking.status);

            return (
              <div
                key={booking.id}
                className="bg-white dark:bg-[#1A1929] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
              >
                {/* Main card */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-white/[0.02] transition-colors"
                  onClick={() => toggleExpand(booking.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Service & Pet */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                          {booking.serviceName || booking.serviceType || t('appointments_appointment', 'Appointment')}
                        </h3>
                        {booking.isEmergency && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full font-medium">
                            {t('appointments_emergency', 'Emergency')}
                          </span>
                        )}
                      </div>

                      {/* Pet name & Business */}
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {booking.petName}
                        {booking.businessName && (
                          <> &middot; {booking.businessName}</>
                        )}
                      </p>

                      {/* Date & Time */}
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(booking.scheduledDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {booking.scheduledTime}
                        </span>
                        {booking.duration && (
                          <span className="text-xs text-gray-400">
                            ({booking.duration} min)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status Badge + Expand */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color} ${config.bgColor}`}>
                        <span>{config.icon}</span>
                        {t(`booking_status_${booking.status}`, config.label)}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Progress Track (for active bookings) */}
                  {!['completed', 'cancelled', 'no_show'].includes(booking.status) && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-1">
                        {ACTIVE_FLOW_STEPS.map((step, idx) => {
                          const currentIdx = getStepIndex(booking.status);
                          const isComplete = currentIdx >= 0 && idx <= currentIdx;
                          const isCurrent = currentIdx === idx;
                          return (
                            <div key={step} className="flex items-center flex-1">
                              <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${
                                isComplete
                                  ? isCurrent
                                    ? 'bg-[#2D1F7B] text-white ring-2 ring-[#2D1F7B]/30'
                                    : 'bg-green-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                              }`}>
                                {isComplete && !isCurrent ? (
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                ) : (
                                  idx + 1
                                )}
                              </div>
                              {idx < ACTIVE_FLOW_STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1 rounded ${
                                  currentIdx >= 0 && idx < currentIdx
                                    ? 'bg-green-500'
                                    : 'bg-gray-200 dark:bg-gray-700'
                                }`} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-1 px-0.5">
                        {ACTIVE_FLOW_STEPS.map((step) => (
                          <span key={step} className="text-[10px] text-gray-400 dark:text-gray-500">
                            {t(`booking_step_${step}`, STATUS_CONFIG[step].label)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-800 p-4 space-y-4 bg-gray-50/50 dark:bg-black/10">
                    {/* Business Contact */}
                    {(booking.businessPhone || booking.businessEmail) && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('appointments_provider', 'Provider')}
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {booking.businessName}
                        </p>
                        {booking.businessPhone && (
                          <p className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <Phone className="w-3.5 h-3.5" />
                            {booking.businessPhone}
                          </p>
                        )}
                        {booking.businessEmail && (
                          <p className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <Mail className="w-3.5 h-3.5" />
                            {booking.businessEmail}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {booking.notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          {t('appointments_notes', 'Notes')}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{booking.notes}</p>
                      </div>
                    )}

                    {/* Completion Notes */}
                    {booking.completionNotes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          {t('appointments_completion_notes', 'Service Notes')}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{booking.completionNotes}</p>
                      </div>
                    )}

                    {/* Cancellation Reason */}
                    {booking.cancellationReason && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-red-600 dark:text-red-400">
                            {t('appointments_cancellation_reason', 'Cancellation Reason')}
                          </p>
                          <p className="text-sm text-red-700 dark:text-red-300">{booking.cancellationReason}</p>
                        </div>
                      </div>
                    )}

                    {/* Price Info */}
                    {(booking.estimatedPrice || booking.finalPrice) && (
                      <div className="flex gap-4">
                        {booking.estimatedPrice && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('appointments_estimated', 'Estimated')}
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              ${booking.estimatedPrice.toFixed(2)}
                            </p>
                          </div>
                        )}
                        {booking.finalPrice && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('appointments_final_price', 'Final Price')}
                            </p>
                            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                              ${booking.finalPrice.toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Status History Timeline */}
                    {statusHistory[booking.id] && statusHistory[booking.id].length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                          {t('appointments_timeline', 'Status Timeline')}
                        </p>
                        <div className="space-y-2">
                          {statusHistory[booking.id].map((entry, idx) => {
                            const toConfig = STATUS_CONFIG[entry.toStatus as BookingStatus] ?? {
                              label: entry.toStatus,
                              color: 'text-gray-500',
                              bgColor: 'bg-gray-100',
                              icon: '📋',
                            };
                            return (
                              <div key={entry.id} className="flex items-start gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`w-3 h-3 rounded-full ${
                                    idx === 0 ? 'bg-[#2D1F7B]' : 'bg-gray-300 dark:bg-gray-600'
                                  }`} />
                                  {idx < statusHistory[booking.id].length - 1 && (
                                    <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
                                  )}
                                </div>
                                <div className="flex-1 -mt-0.5">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {toConfig.icon} {t(`booking_status_${entry.toStatus}`, toConfig.label)}
                                  </p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500">
                                    {new Date(entry.createdAt).toLocaleString()}
                                  </p>
                                  {entry.reason && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 italic">
                                      {entry.reason}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {(() => {
                      const matchingCert = certificates.find((c: any) => c.bookingId === booking.id);
                      const matchingRx = prescriptions.find((p) => p.bookingId === booking.id);
                      const matchingInvoice = invoices.find((i) => i.bookingId === booking.id);
                      return (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {/* Completed + has certificate */}
                          {booking.status === 'completed' && matchingCert && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setViewingCert(matchingCert); }}
                              style={{ backgroundColor: '#059669', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              <Award style={{ width: '16px', height: '16px' }} />
                              View Certificate
                            </button>
                          )}

                          {/* Completed + has prescription */}
                          {booking.status === 'completed' && matchingRx && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setViewingPrescription(matchingRx); }}
                              style={{ backgroundColor: '#2563EB', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              <Pill style={{ width: '16px', height: '16px' }} />
                              View Prescription
                            </button>
                          )}

                          {/* Has invoice — enabled for any booking that generated one */}
                          {matchingInvoice && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setViewingInvoice(matchingInvoice); }}
                              style={{ backgroundColor: '#6D28D9', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              <Receipt style={{ width: '16px', height: '16px' }} />
                              View Invoice
                            </button>
                          )}

                          {/* Completed + no certificate */}
                          {booking.status === 'completed' && !matchingCert && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRequestAction(booking.id, 'request-certificate'); }}
                              disabled={requestingAction === booking.id}
                              style={{ backgroundColor: '#2563EB', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: requestingAction === booking.id ? 0.6 : 1 }}
                            >
                              {requestingAction === booking.id ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : <FileCheck style={{ width: '16px', height: '16px' }} />}
                              Request Certificate
                            </button>
                          )}

                          {/* In Progress */}
                          {booking.status === 'in_progress' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRequestAction(booking.id, 'request-completion'); }}
                              disabled={requestingAction === booking.id}
                              style={{ backgroundColor: '#7C3AED', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: requestingAction === booking.id ? 0.6 : 1 }}
                            >
                              {requestingAction === booking.id ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: '16px', height: '16px' }} />}
                              Request Completion
                            </button>
                          )}

                          {/* Scheduled/Confirmed/Assigned - Reschedule */}
                          {['new', 'pending_assignment', 'assigned', 'confirmed', 'rescheduled'].includes(booking.status) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setReschedulingBooking(booking); }}
                              style={{ backgroundColor: '#F59E0B', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              <CalendarClock style={{ width: '16px', height: '16px' }} />
                              Reschedule
                            </button>
                          )}

                          {/* Cancel */}
                          {canCancel && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancel(booking.id);
                              }}
                              disabled={cancellingId === booking.id}
                              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50 transition-colors"
                            >
                              {cancellingId === booking.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                              {t('appointments_cancel', 'Cancel Appointment')}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {t('appointments_prev', 'Previous')}
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {t('appointments_next', 'Next')}
          </button>
        </div>
      )}
      {/* BookingFlow Modal */}
      {showBooking && (
        <BookingFlow onClose={() => { setShowBooking(false); fetchBookings(); }} />
      )}

      {/* Reschedule Modal */}
      {reschedulingBooking && (
        <RescheduleAppointmentModal
          appointment={{
            id: reschedulingBooking.id,
            type: reschedulingBooking.serviceName || reschedulingBooking.reason || 'Appointment',
            provider: reschedulingBooking.businessName || 'Provider',
            date: reschedulingBooking.scheduledDate,
            time: reschedulingBooking.scheduledTime,
            notes: reschedulingBooking.notes || undefined,
          }}
          onClose={() => setReschedulingBooking(null)}
          onReschedule={async (id, newDate, newTime) => {
            try {
              await bookingsApi.reschedule(id, newDate, to24h(newTime));
              toast.success(t('appointments_rescheduled', 'Appointment rescheduled'));
              setReschedulingBooking(null);
              fetchBookings();
            } catch (err: any) {
              toast.error(err?.response?.data?.error || t('appointments_reschedule_error', 'Reschedule failed'));
            }
          }}
        />
      )}

      {/* Certificate Viewer Modal */}
      {viewingCert && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setViewingCert(null)}
        >
          <div
            style={{ backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '420px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Green Header */}
            <div style={{ background: 'linear-gradient(135deg, #059669, #10B981)', padding: '24px', textAlign: 'center', color: '#fff' }}>
              <ShieldCheck style={{ width: '48px', height: '48px', margin: '0 auto 12px' }} />
              <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Service Certificate</h2>
              <p style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px' }}>Verified by Petso</p>
            </div>

            {/* Certificate Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ border: '2px solid #D1FAE5', borderRadius: '12px', padding: '20px', backgroundColor: '#F0FDF4' }}>
                {viewingCert.petName && (
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Pet</p>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>{viewingCert.petName}</p>
                  </div>
                )}
                {viewingCert.serviceName && (
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Service</p>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>{viewingCert.serviceName}</p>
                  </div>
                )}
                {viewingCert.businessName && (
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Provider</p>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>{viewingCert.businessName}</p>
                  </div>
                )}
                {viewingCert.completedAt && (
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Completed</p>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>{new Date(viewingCert.completedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                )}
                {viewingCert.certificateNumber && (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Certificate #</p>
                    <p style={{ fontSize: '13px', fontWeight: 500, color: '#374151', margin: '2px 0 0', fontFamily: 'monospace' }}>{viewingCert.certificateNumber}</p>
                  </div>
                )}

                {/* QR Code */}
                {viewingCert.verificationUrl && (
                  <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px dashed #A7F3D0' }}>
                    <QRCodeCanvas value={viewingCert.verificationUrl} size={120} />
                    <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '8px' }}>Scan to verify</p>
                  </div>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={() => setViewingCert(null)}
                style={{ width: '100%', marginTop: '16px', padding: '12px', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Prescription Viewer Modal */}
      {viewingPrescription && createPortal(
        <PrescriptionDetailView
          prescription={viewingPrescription}
          onClose={() => setViewingPrescription(null)}
        />,
        document.body,
      )}

      {/* Invoice Viewer Modal */}
      {viewingInvoice && createPortal(
        <InvoiceDetailView
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
        />,
        document.body,
      )}
    </div>
  );
}
